import { memo } from "react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";

import type { ActionDescriptor, SearchHit } from "../api/searchApi";
import { TYPE_ICON } from "../lib/actions";

export const StatusChip = memo(function StatusChip({ status }: { status?: string | null }) {
  if (!status) return null;
  const active = status.toUpperCase() === "ACTIVE";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[11px]",
        active ? "text-success" : "text-muted-foreground"
      )}
    >
      <span
        className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-success" : "bg-muted-foreground/50")}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
});

export const Chip = memo(function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-auto rounded-full px-3 py-1 text-xs font-medium transition",
        active
          ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
          : "bg-card text-muted-foreground border border-border hover:border-primary/40 hover:bg-muted"
      )}
    >
      {children}
    </Button>
  );
});

interface ResultRowProps {
  hit: SearchHit;
  actions: ActionDescriptor[];
  onAction: (hit: SearchHit, d: ActionDescriptor) => void;
  /** Row click / Enter — opens the hit itself, independent of the catalog. */
  onOpen: (hit: SearchHit) => void;
}

export const ResultRow = memo(function ResultRow({
  hit,
  actions,
  onAction,
  onOpen,
}: ResultRowProps) {
  const Icon = TYPE_ICON[hit.type];
  const needsBroker = (k: string) => k === "COMMISSION" || k === "COMMISSION_RULES";
  return (
    // Not a <button>: the per-hit action buttons render inside the row and
    // buttons must not nest. role/tabIndex/keydown give it the same a11y.
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${hit.name || `#${hit.id}`}`}
      onClick={() => onOpen(hit)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(hit);
        }
      }}
      className="group flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground">
            {hit.name || `#${hit.id}`}
          </span>
          <StatusChip status={hit.status} />
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {hit.hierarchyPath || hit.level}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-1 opacity-70 transition group-hover:opacity-100">
        {actions.map((d) => {
          const disabled = needsBroker(d.key) && !hit.brokerId;
          return (
            <Button
              key={d.key}
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onAction(hit, d);
              }}
              title={disabled ? "No broker id" : `${d.label} for ${hit.name}`}
              className="h-auto rounded-md border border-border bg-card px-2 py-1 text-[11px] font-medium text-muted-foreground shadow-sm hover:border-primary/40 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {d.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
});
